import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack,
  Text,
  DefaultButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Icon,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi } from '../hooks/useApi'
import { LifecycleChart } from '../components/charts/LifecycleChart'
import { KpiBand } from '../components/dashboard/KpiBand'
import { PosturePanel } from '../components/dashboard/PosturePanel'
import { RecentChangesFeed } from '../components/dashboard/RecentChangesFeed'
import type { SyncHealth } from '../types'

/**
 * Dashboard v2 — the Lifecycle Command Center (page assembly).
 *
 * Reading order is deliberate, per the locked v2 design:
 *   1. KpiBand        — "is anything on fire?" (the numbers you act on)
 *   2. LifecycleChart — "what changed, when?" (the daily change histogram; the lead)
 *   3. PosturePanel   — "how healthy is the estate?" (credential/ownership/SSO posture)
 *   4. RecentChangesFeed — "what changed most recently?" (per-app, day-grouped)
 *   5. Owner alerts   — the one v1 card that survives: it is *actionable*
 *                       (assign an owner) rather than descriptive.
 *
 * The v1 stat-card grid and the credential-expiration bar chart are gone —
 * superseded by KpiBand and PosturePanel respectively; the expiring-credential
 * *list* lives on the Credentials page.
 *
 * `days` is owned here rather than by the chart because KpiBand's deltas are
 * only meaningful against the same window the chart is showing.
 */

/** "31 hours" reads worse than "1 day" once the gap is large. */
function formatStaleness(hours: number | null): string {
  if (hours == null) return 'some time'
  if (hours < 48) return `${Math.round(hours)} hours`
  return `${Math.round(hours / 24)} days`
}

interface OwnerLifecycleSummary {
  statusCounts: Array<{ accountStatus: string; ownerCount: number }>
  applicationsWithoutOwners: Array<{
    applicationId: number
    displayName: string
    businessUnitName?: string
    riskLevelName?: string
    riskLevelColor?: string
  }>
  applicationsWithoutOwnersCount: number
  recentlyDepartedOwners: Array<{
    ownerId: number
    displayName: string
    email: string
    accountStatus: string
    departedAt: string
  }>
}

export function Dashboard() {
  const navigate = useNavigate()
  const [days, setDays] = useState(30)

  const fetchOwnerSummary = useCallback(() => apiService.getOwnerLifecycleSummary(), [])
  const { data: ownerSummary, loading: ownerLoading, error: ownerError } =
    useApi<OwnerLifecycleSummary>(fetchOwnerSummary)

  const fetchSyncHealth = useCallback(() => apiService.getSyncHealth(), [])
  const { data: syncHealth } = useApi<SyncHealth>(fetchSyncHealth)

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-description">
          SSO lifecycle command center — what changed, what needs attention, and how the estate is trending
        </p>
      </div>

      {/* Stale-sync banner (V11). Everything below is only as fresh as the last
          collector run — if that stalled, say so rather than presenting stale
          counts as current. Failure to fetch health is not itself an alarm. */}
      {syncHealth?.stale && (
        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 16 } }}>
          <strong>Data may be out of date.</strong>{' '}
          {syncHealth.lastSyncAt
            ? `The last successful sync was ${formatStaleness(syncHealth.hoursSinceSync)} ago (${new Date(
                syncHealth.lastSyncAt,
              ).toLocaleString()}).`
            : 'No successful sync has been recorded yet.'}{' '}
          The collectors run daily — if this persists, check the sync job.
        </MessageBar>
      )}

      {/* 1. KPIs — shares the chart's range so the deltas line up */}
      <KpiBand days={days} />

      {/* 2. The lead: daily change histogram. Click a day to route into it. */}
      <div className="card">
        <h2 className="card-title">SSO Lifecycle — Daily Changes</h2>
        <LifecycleChart
          initialDays={days}
          onDaysChange={setDays}
          onDayClick={(date) => navigate(`/changes/${date}`)}
        />
      </div>

      {/* 3. Estate posture */}
      <PosturePanel />

      {/* 4. Recent changes, per app */}
      <RecentChangesFeed />

      {/* 5. Owner alerts — kept from v1 because it is actionable, not just descriptive */}
      {ownerSummary &&
        (ownerSummary.applicationsWithoutOwnersCount > 0 || ownerSummary.recentlyDepartedOwners.length > 0) && (
          <div className="card">
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: 16 }}>
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                <Icon iconName="Warning" styles={{ root: { color: '#8764b8', fontSize: 20 } }} />
                <h2 className="card-title" style={{ margin: 0 }}>Owner Alerts</h2>
              </Stack>
              <DefaultButton text="View All Owners" onClick={() => navigate('/owners')} />
            </Stack>

            {ownerError && <MessageBar messageBarType={MessageBarType.error}>{ownerError}</MessageBar>}

            {ownerLoading ? (
              <Stack horizontalAlign="center" styles={{ root: { padding: 40 } }}>
                <Spinner size={SpinnerSize.large} />
              </Stack>
            ) : (
              <Stack tokens={{ childrenGap: 24 }}>
                {/* Applications Without Owners */}
                {ownerSummary.applicationsWithoutOwnersCount > 0 && (
                  <div>
                    <Text
                      variant="mediumPlus"
                      styles={{ root: { fontWeight: 600, marginBottom: 12, display: 'block' } }}
                    >
                      Applications Without Owners ({ownerSummary.applicationsWithoutOwnersCount})
                    </Text>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #edebe9' }}>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Application</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Business Unit</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Risk Level</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ownerSummary.applicationsWithoutOwners.slice(0, 5).map(app => (
                            <tr key={app.applicationId} style={{ borderBottom: '1px solid #edebe9' }}>
                              <td style={{ padding: '8px', fontWeight: 500 }}>{app.displayName}</td>
                              <td style={{ padding: '8px' }}>{app.businessUnitName || '-'}</td>
                              <td style={{ padding: '8px' }}>
                                {app.riskLevelName && (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 12,
                                      fontSize: 12,
                                      backgroundColor: app.riskLevelColor ? `${app.riskLevelColor}20` : '#f3f2f1',
                                      color: app.riskLevelColor || '#605e5c',
                                    }}
                                  >
                                    {app.riskLevelName}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '8px' }}>
                                <DefaultButton
                                  text="Assign Owner"
                                  onClick={() => navigate(`/applications/${app.applicationId}`)}
                                  styles={{ root: { minWidth: 100, height: 28 } }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {ownerSummary.applicationsWithoutOwnersCount > 5 && (
                        <Text styles={{ root: { marginTop: 8, display: 'block', color: '#605e5c', fontSize: 12 } }}>
                          ...and {ownerSummary.applicationsWithoutOwnersCount - 5} more applications need owners
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {/* Recently Departed Owners */}
                {ownerSummary.recentlyDepartedOwners.length > 0 && (
                  <div>
                    <Text
                      variant="mediumPlus"
                      styles={{ root: { fontWeight: 600, marginBottom: 12, display: 'block' } }}
                    >
                      Recently Departed Owners (Last 30 Days)
                    </Text>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #edebe9' }}>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Name</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Email</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Status</th>
                            <th style={{ textAlign: 'left', padding: '8px', color: '#605e5c' }}>Departed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ownerSummary.recentlyDepartedOwners.slice(0, 5).map(owner => (
                            <tr key={owner.ownerId} style={{ borderBottom: '1px solid #edebe9' }}>
                              <td style={{ padding: '8px', textDecoration: 'line-through', color: '#a19f9d' }}>
                                {owner.displayName}
                              </td>
                              <td style={{ padding: '8px', color: '#a19f9d' }}>{owner.email}</td>
                              <td style={{ padding: '8px' }}>
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: 12,
                                    fontSize: 12,
                                    backgroundColor: '#fed9cc',
                                    color: '#a4262c',
                                  }}
                                >
                                  {owner.accountStatus}
                                </span>
                              </td>
                              <td style={{ padding: '8px', color: '#605e5c' }}>
                                {new Date(owner.departedAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Stack>
            )}
          </div>
        )}
    </div>
  )
}
